// The words of the screens PocketJS draws in the room: the laptop's apps and
// their titles in Pocket Shell, and Pocket TV. English and Japanese, one
// catalog each; test/laptop-words.test.ts holds them to the same keys. Pure,
// so the apps, the TV and the page's laptop host all read it.
//
// Japanese follows the Studio's glossary: です・ます, plain statements, no
// exclamation marks, product and game names as they are, one half-width space
// between Japanese and Latin words or numbers.

import { LANGS, parseLang, type Lang } from "../../../src/core/lang.ts";
import { DEVICE_NAMES, starterTagline } from "../../../src/core/names.ts";

export { LANGS, type Lang };

/** A value a state line carries as the language, or null when it names none this catalog has. */
export function langOf(value: unknown): Lang | null {
  return parseLang(value);
}

const EN = {
  // the apps' titles in Pocket Shell: desktop icons, the Start menu, window captions
  "app.games": "My Games",
  "app.settings": "Settings",
  "app.guide": "Install Guide",

  // shared
  "wait.loading": "Loading",
  "wait.open": "Open this app on the Pocket Studio laptop",
  "wait.studio": "Waiting for your studio",
  "wait.needs": "It needs the studio connection to show your account",
  list: (items: readonly string[]) => items.join(", "),
  /** What stands between two names of a list, the one after the last excepted. */
  "list.separator": ", ",

  // My Games
  "games.count": (n: number) => (n === 1 ? "1 game" : `${n} games`),
  "games.row": (author: string, status: string) => `${author}, ${status}`,
  "games.by": (author: string, status: string) => `by ${author}, ${status}`,
  "games.none": "No games yet.",
  "games.playOn": "Play on",
  "games.playsOn": (device: string) => `Plays on ${device}`,
  "games.noBuildOn": "No build yet. Keep creating on",
  "games.noBuild": "No build yet",
  "games.play": "Play",
  "games.publish": "Publish",
  "games.unpublish": "Unpublish",
  "status.starter": "Starter",
  "status.shared": "Shared",
  "status.takenDown": "Taken down",
  "status.published": "Published",
  "status.draft": "Draft",


  // Settings
  "settings.account": "Account",
  "settings.room": "Room",
  "settings.membership": "Membership",
  "settings.appearance": "Appearance",
  "settings.status": (section: string, value: string) => `${section}: ${value}`,
  "settings.name": "Name",
  "settings.handle": "Handle",
  "settings.state": "Status",
  "settings.guestAccount": "Guest account",
  "settings.signedIn": "Signed in",
  "settings.signOut": "Sign out",
  "settings.roomCurrent": "Current room",
  "settings.roomOwned": "Owned",
  "settings.roomSoon": "Coming soon",
  "settings.roomSoonNote": (room: string) => `${room} is coming soon.`,
  "settings.plan": "Plan",
  "settings.manage": "Manage membership",
  "settings.join": "Become a member",
  "settings.paused": "New memberships are paused while we update the benefits.",
  "user.guest": "Guest",
  "plan.member": "Member",
  "plan.free": "Free",

  // Install Guide
  "guide.introTitle": "Before you start",
  "guide.introDetail": "Homebrew and .zip files",
  "guide.scrollHint": "Up and Down scroll the text",

  // Pocket TV
  "tv.noSignal": "NO SIGNAL",
  "tv.emptyShelf": "Nothing is on the shelf yet.",
  "tv.nowShowing": "NOW SHOWING",
  "tv.by": (author: string) => `by ${author}`,
  "tv.devices": (n: number) => `${n} devices`,
  "tv.tickerEmpty": "POCKET TV  +++  NOTHING ON THE SHELF YET  +++  MAKE A GAME AND PUBLISH IT  +++  ",
  "tv.tickerCount": (n: number) => `${n === 1 ? "1 GAME" : `${n} GAMES`} ON THE SHELF TONIGHT`,
  "tv.tickerGame": (title: string, author: string) => `${title.toUpperCase()} BY ${author.toUpperCase()}`,
  "tv.tickerOpen": "PRESS ENTER TO OPEN A GAME",
  /** What the announcer says about a game; `devices` is already a phrase. */
  "tv.announce": (title: string, author: string, tagline: string, devices: string) =>
    `${title}, by ${author}.${tagline ? ` ${tagline.replace(/[.!?]*$/, ".")}` : ""}${devices ? ` It plays on ${devices}.` : ""}`,
  /** The devices as the announcer names them: up to three by name, more counted. */
  "tv.announceDevices": (names: readonly string[]) => {
    const COUNT = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight"];
    if (names.length > 3) return `${COUNT[names.length] ?? names.length} devices`;
    if (names.length > 2) return `the ${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
    return names.length ? `the ${names.join(" and ")}` : "";
  },
} as const;

/** `text` and the half-width space Japanese puts after a Latin word or a number. */
const spaced = (text: string) => (/[A-Za-z0-9.)]$/.test(text) ? `${text} ` : text);
/** `text` and the half-width space Japanese puts before a Latin word or a number. */
const led = (text: string) => (/^[A-Za-z0-9(]/.test(text) ? ` ${text}` : text);

type Catalog = {
  [K in keyof typeof EN]: (typeof EN)[K] extends (...args: infer A) => string ? (...args: A) => string : string;
};

const JA: Catalog = {
  "app.games": "マイゲーム",
  "app.settings": "設定",
  "app.guide": "インストールガイド",

  "wait.loading": "読み込み中",
  "wait.open": "Pocket Studio のノートパソコンで開いてください",
  "wait.studio": "スタジオからの応答を待っています",
  "wait.needs": "アカウントを表示するには、スタジオとの接続が必要です",
  list: (items) => items.join("、"),
  "list.separator": "、",

  "games.count": (n) => `ゲーム ${n} 本`,
  "games.row": (author, status) => `${author}、${status}`,
  "games.by": (author, status) => `作者 ${author}、${status}`,
  "games.none": "まだゲームはありません。",
  "games.playOn": "遊ぶデバイス",
  "games.playsOn": (device) => `${spaced(device)}で遊べます`,
  "games.noBuildOn": "まだビルドがありません。続きを作るデバイス",
  "games.noBuild": "まだビルドがありません",
  "games.play": "遊ぶ",
  "games.publish": "公開する",
  "games.unpublish": "公開をやめる",
  "status.starter": "スターター",
  "status.shared": "みんなの作品",
  "status.takenDown": "公開停止",
  "status.published": "公開中",
  "status.draft": "下書き",


  "settings.account": "アカウント",
  "settings.room": "部屋",
  "settings.membership": "メンバーシップ",
  "settings.appearance": "外観",
  "settings.status": (section, value) => `${section}：${value}`,
  "settings.name": "名前",
  "settings.handle": "ハンドル",
  "settings.state": "状態",
  "settings.guestAccount": "ゲストアカウント",
  "settings.signedIn": "ログイン中",
  "settings.signOut": "ログアウト",
  "settings.roomCurrent": "使用中",
  "settings.roomOwned": "所有",
  "settings.roomSoon": "準備中",
  "settings.roomSoonNote": (room) => `${spaced(room)}は準備中です。`,
  "settings.plan": "プラン",
  "settings.manage": "メンバーシップを管理",
  "settings.join": "メンバーになる",
  "settings.paused": "特典の見直しに伴い、新規メンバーシップの受付を休止しています。",
  "user.guest": "ゲスト",
  "plan.member": "メンバー",
  "plan.free": "無料",

  "guide.introTitle": "はじめに",
  "guide.introDetail": "自作ソフトと .zip ファイル",
  "guide.scrollHint": "上下キーで文章を送ります",

  "tv.noSignal": "信号なし",
  "tv.emptyShelf": "棚にはまだ何もありません。",
  "tv.nowShowing": "放送中",
  "tv.by": (author) => `作者 ${author}`,
  "tv.devices": (n) => `${n} 台のデバイス`,
  "tv.tickerEmpty": "POCKET TV  +++  棚はまだ空です  +++  ゲームを作って公開すると、ここに並びます  +++  ",
  "tv.tickerCount": (n) => `今夜の棚にはゲームが ${n} 本`,
  "tv.tickerGame": (title, author) => `${title}（${author}）`,
  "tv.tickerOpen": "Enter キーでゲームを開きます",
  "tv.announce": (title, author, tagline, devices) =>
    `${spaced(title)}は${spaced(led(author))}の作品です。${[tagline, devices ? `${spaced(devices)}で遊べます。` : ""].filter(Boolean).join(" ")}`,
  // (Japanese takes more of the caption's three lines: past three devices the
  // card beside the picture counts them and the announcer does not)
  "tv.announceDevices": (names) => (names.length > 3 ? "" : names.join("、")),
};

const CATALOGS: Record<Lang, Catalog> = { en: EN as unknown as Catalog, ja: JA };

/** The catalog of a language. */
export function words(lang: Lang): Catalog {
  return CATALOGS[lang];
}

/** Package ids of the laptop's apps and the key of each one's title. */
export const APP_TITLE_KEYS = {
  "dev.pocket-nexus.studio.games": "app.games",
  "dev.pocket-nexus.studio.settings": "app.settings",
  "dev.pocket-nexus.studio.guide": "app.guide",
} as const satisfies Record<string, keyof Catalog>;

/** The apps' titles in a language, by package id. */
export function appTitles(lang: Lang): Record<string, string> {
  const catalog = words(lang);
  return Object.fromEntries(Object.entries(APP_TITLE_KEYS).map(([id, key]) => [id, catalog[key]]));
}

/**
 * A device's name in a language, from the list the room's DOM reads too
 * (src/core/names.ts); `fallback` for an id that list does not have.
 */
export function deviceName(id: string, lang: Lang, fallback?: string): string {
  return (DEVICE_NAMES[lang] as Record<string, string>)[id] ?? fallback ?? id;
}

/** A game's line in a language: a built-in game's from src/core/names.ts, any other game's as its author wrote it. */
export function taglineOf(app: { id: string; origin?: string; tagline: string }, lang: Lang): string {
  return (app.origin === undefined || app.origin === "starter" ? starterTagline(app.id, lang) : null) ?? app.tagline;
}
